import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";
import Modal from "react-modal";
import toast, { Toaster } from "react-hot-toast";
import { addToLocalCart } from "../services/CartServices";
import "./CategoryProduct.css";

Modal.setAppElement("#root");

const DEBOUNCE_MS = 300;

const CategoryProductPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [categoryName, setCategoryName] = useState("");
  const [products, setProducts] = useState([]);
  const [visible, setVisible] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters / UI state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [sortBy, setSortBy] = useState("featured");
  const [quickProduct, setQuickProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  // fetch category products
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/categories/slug/${slug}`);
        const data = res.data || {};
        const catName = data.categoryName || data.category?.name || slug;
        const prods = data.products || data;
        if (!cancelled) {
          setCategoryName(catName);
          setProducts(Array.isArray(prods) ? prods : []);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        toast.error("Failed to load category products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => (cancelled = true);
  }, [slug]);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // compute min/max price options from products (use discountedPrice if present)
  const priceBounds = useMemo(() => {
    if (!products.length) return { min: 0, max: 10000 };
    const prices = products.map((p) =>
      p.discountedPrice ? Number(p.discountedPrice) : Number(p.price) || 0
    );
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return { min: Math.floor(min), max: Math.ceil(max) };
  }, [products]);

  // initialize price range to product bounds once loaded
  useEffect(() => {
    if (!loading && products.length) {
      const { min, max } = priceBounds;
      setMinPrice(min);
      setMaxPrice(max);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, products.length]);

  // filter + sort visible products
  useEffect(() => {
    let list = [...products];

    // search (name or description)
    if (debouncedSearch) {
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(debouncedSearch) ||
        (p.description || "").toLowerCase().includes(debouncedSearch)
      );
    }

    // price filter (use discountedPrice if present)
    list = list.filter((p) => {
      const price = p.discountedPrice ? Number(p.discountedPrice) : Number(p.price) || 0;
      return price >= Number(minPrice) && price <= Number(maxPrice);
    });

    // sort
    if (sortBy === "price-asc") {
      list.sort((a, b) =>
        (a.discountedPrice ? Number(a.discountedPrice) : Number(a.price) || 0) -
        (b.discountedPrice ? Number(b.discountedPrice) : Number(b.price) || 0)
      );
    } else if (sortBy === "price-desc") {
      list.sort((a, b) =>
        (b.discountedPrice ? Number(b.discountedPrice) : Number(b.price) || 0) -
        (a.discountedPrice ? Number(a.discountedPrice) : Number(a.price) || 0)
      );
    }
    // else featured/default: keep backend order

    setVisible(list);
  }, [products, debouncedSearch, minPrice, maxPrice, sortBy]);

  // open quick view
  const openQuickView = (product) => {
    setQuickProduct(product);
    setIsModalOpen(true);
  };

  // add to cart handler
  const handleAddToCart = (product) => {
    const price = product.discountedPrice ? Number(product.discountedPrice) : Number(product.price);
    addToLocalCart({ ...product, price }, 1);
    toast.success(`${product.name} added to cart`);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // Quick view modal content
  const QuickView = () => {
    if (!quickProduct) return null;
    const p = quickProduct;
    return (
      <div className="quickview-content">
        <div className="quickview-left">
          <img src={p.image || "/assets/product-placeholder.png"} alt={p.name} />
        </div>
        <div className="quickview-right">
          <h3>{p.name}</h3>
          <p className="meta">{p.category?.name || p.categoryName || ""}</p>
          <div className="price">
            ₹{p.discountedPrice ? p.discountedPrice : p.price}
            {p.discountPercent > 0 && (
              <span style={{ color: "red", marginLeft: "8px", fontSize: "0.95em" }}>
                <del>₹{p.price}</del> ({p.discountPercent}% OFF)
              </span>
            )}
          </div>
          <p className="desc">{p.description}</p>
          {/* <div className="quickview-actions">
            <button className="btn btn-primary" onClick={() => handleAddToCart(p)}>Add to cart</button>
            <button className="btn btn-outline" onClick={() => { setIsModalOpen(false); navigate(`/product/${p.id || p.slug}`); }}>
              View details
            </button>
          </div> */}
        </div>
      </div>
    );
  };

  return (
    <div className="category-page">
      <Toaster position="top-right" />

      <header className="category-hero">
        <div className="hero-inner">
          <div className="hero-left">
            <h1 data-aos="fade-right">Category: <span>{categoryName}</span></h1>
            <p data-aos="fade-right" data-aos-delay="120">
              Curated natural & organic picks — handcrafted and gentle.
            </p>
          </div>
        </div>
      </header>

      {/* Sticky Filter Bar */}
      <div className="filter-sticky-wrapper">
        <div className="filter-bar" data-aos="fade-down">
          <div className="filter-left">
            <input
              type="search"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            <span className="search-hint">Type to filter</span>
          </div>
          <div className="filter-center">
            <label className="range-label">Price</label>
            <div className="range-inputs">
              <input type="number" value={minPrice} min={priceBounds.min} max={maxPrice} onChange={(e) => setMinPrice(Number(e.target.value || priceBounds.min))} />
              <span className="range-sep">—</span>
              <input type="number" value={maxPrice} min={minPrice} max={priceBounds.max} onChange={(e) => setMaxPrice(Number(e.target.value || priceBounds.max))} />
            </div>
          </div>
          <div className="filter-right">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low → High</option>
              <option value="price-desc">Price: High → Low</option>
            </select>
            <button className="btn btn-clear" onClick={() => { setSearch(""); const { min, max } = priceBounds; setMinPrice(min); setMaxPrice(max); setSortBy("featured"); }}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Grid / Products */}
      <main className="container-products">
        {loading ? (
          <div className="loading">Loading products…</div>
        ) : visible.length === 0 ? (
          <div className="no-results">
            <h3>No products found</h3>
            <p>Try changing filters or clear search.</p>
          </div>
        ) : (
          <motion.div className="product-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}>
            {visible.map((p, i) => (
              <motion.div
                className="product-card"
                key={p.id ?? i}
                data-aos="fade-up"
                data-aos-delay={i * 60}
                whileHover={{ scale: 1.03 }}
              >
                <div className="thumb" onClick={() => openQuickView(p)}>
                  <img src={p.image || "/assets/product-placeholder.png"} alt={p.name} />
                </div>
                <div className="card-body">
                  <h4 className="prod-title">{p.name}</h4>
                  <div className="prod-meta">
                    <span className="price">
                      ₹{p.discountedPrice ? p.discountedPrice : p.price}
                      {p.discountPercent > 0 && (
                        <span style={{ color: "red", marginLeft: "8px", fontSize: "0.95em" }}>
                          <del>₹{p.price}</del> ({p.discountPercent}% OFF)
                        </span>
                      )}
                    </span>
                    <span className="stock">{p.stock > 0 ? "In stock" : "Out of stock"}</span>
                  </div>
                  <div className="card-actions">
                    <button className="btn btn-ghost" onClick={() => openQuickView(p)}>Quick view</button>
                    <button className="btn btn-primary" onClick={() => handleAddToCart(p)}>Add to cart</button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>

      {/* Quick View Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        overlayClassName="modal-overlay"
        className="modal-body"
      >
        <button className="modal-close" onClick={() => setIsModalOpen(false)}>✕</button>
        {quickProduct ? <QuickView /> : <div>Loading…</div>}
      </Modal>
    </div>
  );
};

export default CategoryProductPage;
